import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { INITIAL_PAGING, TPaging } from '@core/interfaces/TPaging';
import { Subscription } from 'rxjs';

import { TFolderItem } from '@core/interfaces/TFolderItem';
import { TSearchResponseItem } from '@core/interfaces/TSearch';
import { TSourceItem } from '@core/interfaces/TSourceItem';

import { LocalSourceButtonComponent } from '../../../../../shared/components/local-sources/local-source-button/local-source-button.component';
import { FoldersService } from '@shared/services/folders.service';
import { LocalSourcesService } from '@shared/services/local-sources.service';
import { SearchService } from '@shared/services/search.service';
import { initialPaging } from '@shared/utils/paging';

export type SourceModalViewMode = 'folders' | 'folder-sources' | 'search';

@Component({
  selector: 'app-source-search-modal',
  templateUrl: './source-search-modal.component.html',
  styleUrls: ['./source-search-modal.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    ReactiveFormsModule,
    MatInput,
    MatIconButton,
    MatIcon,
    CdkScrollable,
    MatDialogContent,
    MatProgressSpinner,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    LocalSourceButtonComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatTooltip,
  ],
})
export class SourceSearchModalComponent implements OnInit, OnDestroy {
  @Input() botId!: string;
  @Output() sourceSelected = new EventEmitter<TSourceItem>();
  @Output() sourceAdded = new EventEmitter<void>();

  viewMode: SourceModalViewMode = 'folders';

  // Folder browse state
  folders: TFolderItem[] = [];
  foldersLoading = false;
  selectedFolder: TFolderItem | null = null;
  folderSources: TSourceItem[] = [];
  folderSourcesLoading = false;
  folderSourcesPaging: TPaging = { ...INITIAL_PAGING, limit: 10 };

  // Search state
  isSearchLoading = false;
  paging = initialPaging();
  searchResults: TSearchResponseItem<TSourceItem> = {} as TSearchResponseItem<TSourceItem>;

  public searchForm = this.fb.nonNullable.group({
    query: [''],
  });

  private _subs: Subscription[] = [];

  constructor(
    private _searchService: SearchService,
    private _foldersService: FoldersService,
    private fb: FormBuilder,
    private _localSourcesService: LocalSourcesService
  ) {}

  ngOnInit(): void {
    this._subs.push(
      this._searchService.getSearchLoadingObservable().subscribe(res => {
        this.isSearchLoading = res;
      })
    );

    this._subs.push(
      this._searchService.getSearchResponseObservable().subscribe(res => {
        if (res) {
          this.searchResults = res.sources;
          this.paging = { totalResults: res.totalResults ?? 0, page: res.page ?? 1 };
        }
      })
    );

    this._subs.push(
      this._foldersService.getFoldersObservable().subscribe(folders => {
        this.folders = folders;
      })
    );

    this._subs.push(
      this._foldersService.getFoldersLoadingObservable().subscribe(loading => {
        this.foldersLoading = loading;
      })
    );

    this._subs.push(
      this._foldersService.getFolderSourcesObservable().subscribe(sources => {
        this.folderSources = sources;
        this.folderSourcesLoading = false;
      })
    );

    this._subs.push(
      this._foldersService.getFolderSourcesPagingObservable().subscribe(paging => {
        this.folderSourcesPaging = paging;
      })
    );

    this._foldersService.fetchFolders();
  }

  // -- Folder browsing --

  selectFolder(folder: TFolderItem): void {
    this.selectedFolder = folder;
    this.viewMode = 'folder-sources';
    this.folderSourcesLoading = true;
    this._foldersService.getFolderSources(folder._id, 1, 10, '', 'name');
  }

  goBackToFolders(): void {
    this.selectedFolder = null;
    this.folderSources = [];
    this.viewMode = 'folders';
  }

  handleFolderSourcesPageEvent(e: PageEvent): void {
    if (!this.selectedFolder) return;
    this.folderSourcesLoading = true;
    this._foldersService.getFolderSources(this.selectedFolder._id, e.pageIndex + 1, 10, '', 'name');
  }

  // -- Search --

  public search(): void {
    this.viewMode = 'search';
    this.paging = initialPaging();
    this._searchService.search(
      'sources',
      this.searchForm.get('query')?.value ?? '',
      this.paging.page
    );
  }

  public clearSearch(): void {
    this.searchForm.get('query')?.setValue('');
    this.searchResults = {} as TSearchResponseItem<TSourceItem>;
    this.paging = initialPaging();
    this.viewMode = this.selectedFolder ? 'folder-sources' : 'folders';
  }

  handlePageEvent(e: PageEvent) {
    this.paging.page = e.pageIndex + 1;
    this._searchService.search(
      'sources',
      this.searchForm.get('query')?.value ?? '',
      this.paging.page
    );
  }

  // -- Source actions --

  onSourceClick(source: TSourceItem): void {
    this.sourceSelected.emit(source);
  }

  onSourceAdded(): void {
    this.sourceAdded.emit();
  }

  isSourceAdded(source: TSourceItem): boolean {
    return this._localSourcesService.isItemInList(this.botId, source._id);
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  // -- File type helpers --

  public getFileType(source: TSourceItem): string {
    const fileName = source.originalFileName || source.fileUrl;
    if (!fileName) return '';

    const extension = fileName.toLowerCase().split('.').pop();
    if (!extension) return '';

    const fileTypeMap: { [key: string]: string } = {
      'pdf': 'PDF',
      'doc': 'Word',
      'docx': 'Word',
      'xls': 'Excel',
      'xlsx': 'Excel',
      'ppt': 'PowerPoint',
      'pptx': 'PowerPoint',
      'txt': 'Text',
      'rtf': 'RTF',
      'csv': 'CSV',
      'png': 'Image',
      'jpg': 'Image',
      'jpeg': 'Image',
      'gif': 'Image',
      'svg': 'Image',
      'mp4': 'Video',
      'avi': 'Video',
      'mov': 'Video',
      'zip': 'Archive',
      'rar': 'Archive',
      '7z': 'Archive',
      'html': 'HTML',
      'htm': 'HTML',
    };

    return fileTypeMap[extension] || extension.toUpperCase();
  }

  public getFileTypeIcon(source: TSourceItem): string {
    const fileName = source.originalFileName || source.fileUrl;
    if (!fileName) return 'description';

    const extension = fileName.toLowerCase().split('.').pop();
    if (!extension) return 'description';

    const iconMap: { [key: string]: string } = {
      'pdf': 'picture_as_pdf',
      'doc': 'description',
      'docx': 'description',
      'xls': 'table_chart',
      'xlsx': 'table_chart',
      'ppt': 'slideshow',
      'pptx': 'slideshow',
      'txt': 'text_snippet',
      'rtf': 'text_snippet',
      'csv': 'table_chart',
      'png': 'image',
      'jpg': 'image',
      'jpeg': 'image',
      'gif': 'image',
      'svg': 'image',
      'mp4': 'movie',
      'avi': 'movie',
      'mov': 'movie',
      'zip': 'folder_zip',
      'rar': 'folder_zip',
      '7z': 'folder_zip',
      'html': 'web',
      'htm': 'web',
    };

    return iconMap[extension] || 'description';
  }
}
